import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { ipApi } from '../services/api';
import { resolveHospitalForLetterhead } from '../utils/hospitalProfile';
import { STORAGE_KEY } from './session';

const HospitalProfileContext = createContext(null);

export function HospitalProfileProvider({ children }) {
  const { user, isLoggedIn } = useAuth();
  const [raw, setRaw] = useState(() => user?.hospitalProfile || null);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const reload = useCallback(async () => {
    if (!isLoggedIn) return;
    setLoading(true);
    setError('');
    try {
      const data = await ipApi.getStaffDirectory();
      setRaw(data.hospital || null);
      setDoctors(data.doctors || []);
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
          const session = JSON.parse(stored);
          session.hospitalProfile = data.hospital;
          localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
        }
      } catch {
        /* ignore */
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [isLoggedIn]);

  useEffect(() => {
    if (isLoggedIn) reload();
    else {
      setRaw(null);
      setDoctors([]);
    }
  }, [isLoggedIn, user?.hospitalId, reload]);

  const hospital = useMemo(
    () => resolveHospitalForLetterhead(raw || user?.hospitalProfile, user),
    [raw, user]
  );

  const value = useMemo(
    () => ({ hospital, raw, doctors, loading, error, reload }),
    [hospital, raw, doctors, loading, error, reload]
  );

  return (
    <HospitalProfileContext.Provider value={value}>
      {children}
    </HospitalProfileContext.Provider>
  );
}

export function useHospitalProfile() {
  const ctx = useContext(HospitalProfileContext);
  if (ctx) return ctx;
  return { hospital: null, raw: null, doctors: [], loading: false, error: '', reload: async () => {} };
}
